import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, staticUrl } from "@/lib/api";
import { Loader2, Dna, Eye, Star, ShieldCheck } from "lucide-react";

const SORTS = [
    { value: "similarity", label: "Most Similar" },
    { value: "viewed", label: "Most Viewed" },
    { value: "credible", label: "Highest Evidence" },
    { value: "recent", label: "Most Recent" },
];

export default function EncounterMatches() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState("similarity");

    useEffect(() => {
        setLoading(true);
        api.get(`/encounters/${id}/matches`, { params: { sort, limit: 50 } })
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [id, sort]);

    if (loading || !data) return <div className="ok-container py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

    return (
        <div className="ok-container py-10 max-w-5xl mx-auto" data-testid="encounter-matches-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 flex items-center gap-1.5">
                        <Dna className="w-3 h-3 text-[var(--ok-accent)]" /> Encounter DNA
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">
                        <span className="text-[var(--ok-accent)] font-display font-black">{data.total}</span> Similar encounters found
                    </h1>
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="ok-input w-auto py-2 text-sm" data-testid="matches-sort">
                    {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
            </div>

            {/* DNA panel */}
            <div className="ok-card p-5 mb-8" data-testid="matches-dna-panel">
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">Encounter genome</div>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(data.dna).map(([k, v]) => {
                        if (!v || v === "" || v === false || (Array.isArray(v) && v.length === 0)) return null;
                        const display = Array.isArray(v) ? v.join(", ") : String(v);
                        return (
                            <span key={k} className="ok-tag" data-testid={`dna-tag-${k}`}>
                                <span className="opacity-60 mr-1">{k.replace(/_/g, " ")}:</span>
                                <strong>{display}</strong>
                            </span>
                        );
                    })}
                </div>
            </div>

            {/* Results */}
            <div className="space-y-3" data-testid="matches-results">
                {data.items.length === 0 ? (
                    <div className="ok-card p-12 text-center text-sm opacity-60">No matches yet. This encounter is unique in the archive.</div>
                ) : (
                    data.items.map((m) => (
                        <Link key={m.id} to={`/encounters/${m.id}`} className="ok-card ok-card-hover p-4 flex gap-4 items-center group" data-testid={`match-${m.id}`}>
                            <div className="w-20 h-20 flex-shrink-0 rounded-sm overflow-hidden border" style={{ borderColor: "var(--ok-border)" }}>
                                <img src={staticUrl(m.sketch_url) || "https://static.prod-images.emergentagent.com/jobs/fd94d242-06a5-4af0-93ba-0c141315848a/images/9bfbab4035d0ff1ff074c7aafc01237c0a827a32f863078809cf41a80484f384.png"}
                                     alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">
                                        {m.match_score} match score
                                    </div>
                                    {(m.tags || []).slice(0, 3).map((t) => <span key={t} className="ok-tag">#{t}</span>)}
                                </div>
                                <div className="font-display font-bold text-base mt-1 group-hover:text-[var(--ok-accent)]">{m.title}</div>
                                <div className="text-xs opacity-60 mt-0.5">
                                    {(m.encounter_date || "").slice(0, 10) || "Unknown date"} · {m.location || m.country}
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs opacity-70 flex-shrink-0">
                                <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[var(--ok-accent)]" /> {m.credibility_score || 0}</span>
                                <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {m.view_count || 0}</span>
                                <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-[var(--ok-accent)]" /> {(m.rating_avg || 0).toFixed(1)}</span>
                            </div>
                        </Link>
                    ))
                )}
            </div>
        </div>
    );
}
